// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { MessageStatus } from '@internal/acs-ui-common';
import React, { useMemo } from 'react';
import { MessageProps, MessageRenderer, MessageThreadStyles, _ChatMessageProps } from '../MessageThread';
import { ChatMessage, OnRenderAvatarCallback } from '../../types';
import { AttachmentMenuAction } from '../../types/Attachment';
import { AttachmentMetadata } from '@internal/acs-ui-common';
import { MessageStatusIndicatorProps } from '../MessageStatusIndicator';
import { FluentChatMessageComponentWrapperProps } from './MessageComponents/FluentChatMessageComponent';
import { DefaultSystemMessage } from './DefaultSystemMessage';
import { InlineImageOptions } from './ChatMessageContent';
import { FluentChatMyMessageComponent } from './MyMessageComponents/FluentChatMyMessageComponent';
import { FluentChatMessageComponent } from './MessageComponents/FluentChatMessageComponent';

/**
 * Props for {@link ChatMessageComponentWrapper}
 *
 * @private
 */
export type ChatMessageComponentWrapperProps = _ChatMessageProps & {
  /**
   * UserId of the current user.
   */
  userId: string;
  styles: MessageThreadStyles | undefined;
  shouldOverlapAvatarAndMessage: boolean;
  onRenderMessageStatus: ((messageStatusIndicatorProps: MessageStatusIndicatorProps) => JSX.Element | null) | undefined;
  defaultStatusRenderer: (message: ChatMessage, participantCount: number, readCount: number, status?: MessageStatus) => JSX.Element;
  onRenderMessage?: (messageProps: MessageProps, messageRenderer?: MessageRenderer) => JSX.Element;
  onRenderAvatar?: OnRenderAvatarCallback;
  showMessageStatus?: boolean;
  participantCount?: number;
  readCount?: number;
  onActionButtonClick: (message: ChatMessage, setMessageReadBy: (readBy: {
    id: string;
    displayName: string;
  }[]) => void) => void;
  inlineImageOptions?: InlineImageOptions;
  onRenderAttachmentDownloads?: (message: ChatMessage) => JSX.Element;
  /**
   * Optional callback to define custom actions for attachments.
   */
  actionsForAttachment?: (attachment: AttachmentMetadata, message?: ChatMessage) => AttachmentMenuAction[];
};

/**
 * The wrapper component to display different types of chat message.
 *
 * @private
 */
export const ChatMessageComponentWrapper = (props: ChatMessageComponentWrapperProps): JSX.Element => {
  const {
    message,
    styles,
    onRenderMessage,
    key: messageKey
  } = props;
  const systemMessageStyle = useMemo(() => {
    return {
      paddingTop: '0.5rem'
    };
  }, []);
  const customMessageStyle = useMemo(() => {
    return {
      paddingTop: '1rem',
      paddingBottom: '0.25rem'
    };
  }, []);
  switch (message.messageType) {
    case 'chat':
      {
        const myChatMessageStyle = message.status === 'failed' ? styles?.failedMyChatMessageContainer ?? styles?.myChatMessageContainer : styles?.myChatMessageContainer;
        const chatMessageStyle = styles?.chatMessageContainer;
        const messageContainerStyle = message.mine ? myChatMessageStyle : chatMessageStyle;
        return fluentChatComponent({
          ...props,
          message: message,
          messageContainerStyle: messageContainerStyle
        });
      }
    case 'system':
      {
        const messageContainerStyle = styles?.systemMessageContainer;
        const systemMessageComponent = onRenderMessage === undefined ? <DefaultSystemMessage {...props} /> : onRenderMessage({
          ...props,
          messageContainerStyle
        }, props => <DefaultSystemMessage {...props} />);
        return <div key={messageKey} style={systemMessageStyle}>
          {systemMessageComponent}
        </div>;
      }
    default:
      {
        // We do not handle custom type message by default, users can handle custom type by using onRenderMessage function.
        const customMessageComponent = onRenderMessage === undefined ? <></> : onRenderMessage({
          ...props
        });
        return <div key={messageKey} style={customMessageStyle}>
          {customMessageComponent}
        </div>;
      }
  }
};
const fluentChatComponent = (props: FluentChatMessageComponentWrapperProps): JSX.Element => {
  if (props.message.mine === true) {
    return <FluentChatMyMessageComponent {...props} />;
  } else {
    return <FluentChatMessageComponent {...props} />;
  }
};